import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, Users, BookOpen, ArrowRight } from 'lucide-react';
import { TherapistFilter } from '../components/search/TherapistFilter';

const FeatureCard = ({ 
  icon: Icon, 
  title, 
  description 
}: { 
  icon: React.ElementType, 
  title: string, 
  description: string 
}) => (
  <motion.div 
    className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 group"
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
  >
    <div className="flex items-center justify-center h-16 w-16 rounded-full bg-purple-100 text-purple-600 mx-auto mb-4 group-hover:bg-purple-200 transition-colors">
      <Icon className="h-8 w-8" />
    </div>
    <h3 className="mt-4 text-xl font-semibold text-gray-900 text-center mb-2">
      {title}
    </h3>
    <p className="text-base text-gray-600 text-center mb-4">
      {description}
    </p>
    <div className="flex justify-center">
      <Link 
        to="#" 
        className="text-purple-600 hover:text-purple-800 flex items-center group-hover:underline"
      >
        Learn More 
        <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
      </Link>
    </div>
  </motion.div>
);

export const HomePage = () => {

  return (
    <>
      <Helmet>
        <title>TherapistDirect | Find Licensed Therapists & Mental Health Professionals</title>
        <meta name="description" content="Find licensed therapists, counselors, and mental health professionals near you. Search by specialty, insurance, location, and availability. Get matched with the right therapist for anxiety, depression, trauma, relationships, and more. Book appointments online." />
        <meta name="keywords" content="find therapist, mental health professional, counselor, therapist near me, online therapy, licensed therapist, mental health services, anxiety therapy, depression therapy, trauma therapy, relationship counseling" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="TherapistDirect" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://therapistdirect.org/" />
        <meta property="og:title" content="TherapistDirect | Find Licensed Therapists & Mental Health Professionals" />
        <meta property="og:description" content="Find licensed therapists, counselors, and mental health professionals near you. Search by specialty, insurance, location, and availability." />
        <meta property="og:image" content="https://therapistdirect.org/images/og-image.jpg" />
        <meta property="og:site_name" content="TherapistDirect" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://therapistdirect.org/" />
        <meta property="twitter:title" content="TherapistDirect | Find Licensed Therapists & Mental Health Professionals" />
        <meta property="twitter:description" content="Find licensed therapists, counselors, and mental health professionals near you. Search by specialty, insurance, location, and availability." />
        <meta property="twitter:image" content="https://therapistdirect.org/images/twitter-image.jpg" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://therapistdirect.org/" />
        
        {/* Schema.org structured data for the main website */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "TherapistDirect",
            "url": "https://therapistdirect.org/",
            "description": "Find licensed therapists, counselors, and mental health professionals near you. Search by specialty, insurance, location, and availability.",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://therapistdirect.org/find-therapist?search={search_term_string}",
              "query-input": "required name=search_term_string"
            },
            "publisher": {
              "@type": "Organization",
              "name": "TherapistDirect",
              "url": "https://therapistdirect.org/",
              "logo": {
                "@type": "ImageObject",
                "url": "https://therapistdirect.org/images/logo.png"
              }
            }
          })}
        </script>
        
        {/* Schema.org structured data for the organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "TherapistDirect",
            "url": "https://therapistdirect.org/",
            "logo": "https://therapistdirect.org/images/logo.png",
            "description": "TherapistDirect connects individuals with licensed mental health professionals for personalized therapy and counseling services.",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "US"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "url": "https://therapistdirect.org/contact"
            },
            "sameAs": [
              "https://www.facebook.com/therapistdirect",
              "https://twitter.com/therapistdirect",
              "https://www.linkedin.com/company/therapistdirect"
            ]
          })}
        </script>
        
        {/* Schema.org structured data for the service */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Therapist Matching Service",
            "description": "Connect with licensed therapists, counselors, and mental health professionals",
            "provider": {
              "@type": "Organization",
              "name": "TherapistDirect"
            },
            "serviceType": "Mental Health Services",
            "areaServed": "United States",
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Therapy Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Individual Therapy"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Couples Counseling"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Family Therapy"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Online Therapy"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Anxiety Treatment"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Depression Treatment"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Trauma Therapy"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Relationship Counseling"
                  }
                }
              ]
            }
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        {/* Hero Section */}
      <motion.div 
        className="bg-white"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="text-center">
            <motion.h1 
              className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <span className="block">Find the right therapist</span>
              <span className="block text-purple-600">for your mental wellness</span>
            </motion.h1>
            
            <motion.p 
              className="mt-3 max-w-2xl mx-auto text-base text-gray-600 sm:text-lg md:mt-5 md:text-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              Personalized mental health support tailored to your unique journey. 
              Connect with compassionate professionals who understand your needs.
            </motion.p>
          </div>
        </div>
      </motion.div>

      {/* Filter Section */}
      <motion.div 
        className="bg-gray-50 py-16"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <TherapistFilter />
        </div>
      </motion.div>

      {/* Features Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="grid grid-cols-1 gap-8 md:grid-cols-3"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0 },
              visible: { 
                opacity: 1,
                transition: {
                  staggerChildren: 0.2
                }
              }
            }}
          >
            <FeatureCard 
              icon={Search}
              title="Find a Therapist"
              description="Discover licensed professionals matched to your specific mental health needs and preferences."
            />
            
            <FeatureCard 
              icon={Users}
              title="Group Practices"
              description="Explore comprehensive care options through established, collaborative mental health practices."
            />
            
            <FeatureCard 
              icon={BookOpen}
              title="Mental Health Blog"
              description="Access expert insights, coping strategies, and supportive resources for holistic wellness."
            />
          </motion.div>
        </div>
      </div>

      {/* Call to Action */}
      <motion.div 
        className="bg-purple-600 text-white py-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold sm:text-4xl mb-6">
            Ready to Start Your Mental Wellness Journey?
          </h2>
          <p className="mt-4 text-lg text-purple-100 mb-8">
            Take the first step towards a healthier, happier you. 
            Connect with a therapist who truly understands.
          </p>
          <div className="flex justify-center space-x-4">
            <Link 
              to="/signup" 
              className="px-6 py-3 bg-white text-purple-600 font-semibold rounded-lg hover:bg-purple-50 transition-colors"
            >
              Get Started
            </Link>
            <Link 
              to="/about" 
              className="px-6 py-3 border border-white text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Learn More
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
    </>
  );
};

export default HomePage;